/**
 * Overall Statistics Block - Widget 1
 * 
 * Displays high-level metrics: time spent, keystrokes, accuracy, speed, WPM
 * Shows trends and improvements with visual indicators
 */

import React from 'react';
import { OverallStats, TimePeriod } from '@/types/statistics';

interface OverallStatsBlockProps {
  data: OverallStats | null;
  loading: boolean;
  period: TimePeriod;
}

const OverallStatsBlock: React.FC<OverallStatsBlockProps> = ({ 
  data, 
  loading, 
  period 
}) => {
  
  // 🔧 DEBUG: Log what we're receiving
  console.log('📊 OverallStatsBlock debug:', {
    hasData: !!data,
    loading,
    data: data ? {
      timeSpent: data.timeSpent,
      sessionsCount: data.sessionsCount,
      accuracy: data.accuracy,
      wpm: data.wpm
    } : null
  });
  
  if (loading) {
    console.log('📊 Showing loading state');
    return <OverallStatsSkeletonBlock />;
  }
  
  if (!data) {
    console.log('📊 No data - showing empty state');
    return <EmptyOverallStatsBlock period={period} />;
  }
  
  console.log('📊 Rendering full widget with data');
  
  const formatTime = (ms: number): string => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };
  
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  const getTrendIcon = (trend: string): string => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      default: return '📊';
    }
  };
  

  
  const getPerformanceColor = (metric: 'accuracy' | 'wpm', value: number): string => {
    if (metric === 'accuracy') {
      if (value >= 98) return 'text-green-600';
      if (value >= 95) return 'text-blue-600';
      if (value >= 90) return 'text-yellow-600';
      return 'text-red-600';
    } else { // wpm
      if (value >= 60) return 'text-green-600';
      if (value >= 40) return 'text-blue-600';
      if (value >= 25) return 'text-yellow-600';
      return 'text-red-600';
    }
  };
  
  const stats = [
    {
      icon: '⏱️',
      label: 'Practice Time',
      value: formatTime(data.timeSpent),
      subtitle: `${data.sessionsCount} sessions`,
      trend: data.trend,
      showTrend: true
    },
    {
      icon: '⌨️',
      label: 'Total Keystrokes',
      value: formatNumber(data.totalKeystrokes),
      subtitle: 'characters typed',
      trend: data.trend,
      showTrend: false
    },
    {
      icon: '🎯',
      label: 'Accuracy',
      value: `${data.accuracy}%`,
      subtitle: getAccuracyLabel(data.accuracy),
      trend: data.trend,
      showTrend: true,
      valueColor: getPerformanceColor('accuracy', data.accuracy),
      improvement: data.improvementPercent
    },
    {
      icon: '⚡',
      label: 'Avg Speed',
      value: `${data.avgSpeed}`,
      subtitle: 'letters/sec',
      trend: data.trend,
      showTrend: true
    },
    {
      icon: '📝',
      label: 'Words/Min',
      value: data.wpm.toString(),
      subtitle: getWPMLabel(data.wpm),
      trend: data.trend,
      showTrend: true,
      valueColor: getPerformanceColor('wpm', data.wpm)
    },
    {
      icon: '🏆',
      label: 'Best 5-Letter',
      value: data.bestWord5 > 0 ? `${data.bestWord5}ms` : '--',
      subtitle: 'fastest time',
      trend: 'stable',
      showTrend: false
    }
  ];
  
  return (
    <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            📊 Overall Progress
          </h2>
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', margin: 0 }}>
            {getPeriodDescription(period)} • {data.sessionsCount} sessions
          </p>
        </div>
        
        {/* Overall Trend Indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '16px' }}>
            {getTrendIcon(data.trend)}
          </span>
          {data.improvementPercent !== 0 && (
            <div style={{ fontSize: '11px', fontWeight: '500', color: data.improvementPercent > 0 ? '#22c55e' : '#ef4444' }}>
              {data.improvementPercent > 0 ? '+' : ''}{data.improvementPercent.toFixed(1)}%
            </div>
          )}
        </div>
      </div>
      
      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px', marginBottom: '12px' }}>
        {stats.map((stat, index) => (
          <div key={index} style={{ background: '#334155', borderRadius: '6px', padding: '8px', textAlign: 'center', border: '1px solid #475569' }}>
            {/* Icon */}
            <div style={{ fontSize: '16px', marginBottom: '4px' }}>{stat.icon}</div>
            
            {/* Label */}
            <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '4px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {stat.label}
            </div>
            
            {/* Value */}
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
              {stat.value}
              {stat.showTrend && (
                <span style={{ fontSize: '10px', color: data.trend === 'up' ? '#22c55e' : data.trend === 'down' ? '#ef4444' : '#94a3b8' }}>
                  {getTrendIcon(stat.trend)}
                </span>
              )}
            </div>
            
            {/* Subtitle */}
            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>
              {stat.subtitle}
            </div>
          </div>
        ))}
      </div>
      
      {/* Performance Insights */}
      <div style={{ paddingTop: '12px', borderTop: '1px solid #475569' }}>
        <h3 style={{ fontSize: '11px', fontWeight: '500', color: '#e2e8f0', marginBottom: '6px' }}>Quick Insights</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          <PerformanceInsight data={data} />
        </div>
      </div>
    </div>
  );
};

// Individual Stat Card Component - Commented out as unused
// const StatCard: React.FC<{
//   icon: string;
//   label: string;
//   value: string;
//   subtitle: string;
//   trend: string;
//   showTrend: boolean;
//   valueColor?: string;
//   improvement?: number;
// }> = ({
//   icon,
//   label,
//   value,
//   subtitle,
//   trend,
//   showTrend,
//   valueColor = 'text-gray-900',
//   improvement
// }) => {
//
//   const getTrendIcon = (trend: string): string => {
//     switch (trend) {
//       case 'up': return '📈';
//       case 'down': return '📉';
//       default: return '📊';
//     }
//   };
//
//   const getTrendColor = (trend: string): string => {
//     switch (trend) {
//       case 'up': return 'text-green-600';
//       case 'down': return 'text-red-600';
//       default: return 'text-gray-500';
//     }
//   };
//
//   return (
//     <div className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition-colors">
//       {/* Icon */}
//       <div className="text-2xl mb-2">{icon}</div>
//
//       {/* Label */}
//       <div className="text-xs text-gray-600 mb-2 font-medium uppercase tracking-wide">
//         {label}
//       </div>
//
//       {/* Value */}
//       <div className={`text-lg font-bold ${valueColor} flex items-center justify-center gap-1`}>
//         {value}
//         {showTrend && (
//           <span className={`text-sm ${getTrendColor(trend)}`}>
//             {getTrendIcon(trend)}
//           </span>
//         )}
//       </div>
//
//       {/* Subtitle */}
//       <div className="text-xs text-gray-500 mt-1">
//         {subtitle}
//       </div>
//
//       {/* Improvement Indicator */}
//       {improvement !== undefined && improvement !== 0 && (
//         <div className={`text-xs mt-1 ${improvement > 0 ? 'text-green-600' : 'text-red-600'}`}>
//           {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}%
//         </div>
//       )}
//     </div>
//   );
// };

// Performance Insights Component
const PerformanceInsight: React.FC<{ data: OverallStats }> = ({ data }) => {
  const insights: Array<{ text: string; type: 'success' | 'warning' | 'info' }> = [];
  
  // Accuracy insights
  if (data.accuracy >= 98) {
    insights.push({ text: '🎯 Excellent accuracy!', type: 'success' });
  } else if (data.accuracy >= 95) {
    insights.push({ text: '👍 Great accuracy', type: 'success' });
  } else if (data.accuracy < 85) {
    insights.push({ text: '⚠️ Focus on accuracy', type: 'warning' });
  }
  
  // Speed insights
  if (data.wpm >= 60) {
    insights.push({ text: '🚀 Expert typing speed!', type: 'success' });
  } else if (data.wpm >= 40) {
    insights.push({ text: '⚡ Advanced speed', type: 'success' });
  } else if (data.wpm < 25) {
    insights.push({ text: '🐌 Work on building speed', type: 'info' });
  }
  
  // Improvement insights
  if (data.improvementPercent > 10) {
    insights.push({ text: '📈 Amazing progress!', type: 'success' });
  } else if (data.improvementPercent < -10) {
    insights.push({ text: '📉 Performance declined', type: 'warning' });
  }
  
  // Practice volume insights
  if (data.sessionsCount >= 50) {
    insights.push({ text: '💪 Great practice consistency', type: 'success' });
  } else if (data.sessionsCount < 10) {
    insights.push({ text: '🎯 Keep practicing regularly', type: 'info' });
  }
  
  const getInsightStyle = (type: string) => {
    const baseStyle = {
      fontSize: '10px',
      padding: '3px 8px',
      borderRadius: '12px',
      fontWeight: '500'
    };
    
    switch (type) {
      case 'success':
        return { ...baseStyle, background: '#166534', color: '#dcfce7' };
      case 'warning':
        return { ...baseStyle, background: '#92400e', color: '#fef3c7' };
      default:
        return { ...baseStyle, background: '#1e40af', color: '#dbeafe' };
    }
  };
  
  return (
    <>
      {insights.map((insight, index) => (
        <span
          key={index}
          style={getInsightStyle(insight.type)}
        >
          {insight.text}
        </span>
      ))}
      {insights.length === 0 && (
        <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '12px', background: '#475569', color: '#e2e8f0' }}>
          🎯 Keep practicing to see insights
        </span>
      )}
    </>
  );
};

// Helper Functions
function getAccuracyLabel(accuracy: number): string {
  if (accuracy >= 98) return 'Near Perfect';
  if (accuracy >= 95) return 'Excellent';
  if (accuracy >= 90) return 'Good';
  if (accuracy >= 85) return 'Fair';
  return 'Needs Work';
}

function getWPMLabel(wpm: number): string {
  if (wpm >= 60) return 'Expert';
  if (wpm >= 40) return 'Advanced';
  if (wpm >= 25) return 'Intermediate';
  return 'Beginner';
}

function getPeriodDescription(period: TimePeriod): string {
  switch (period) {
    case 'week': return 'Last 7 days';
    case 'month': return 'Last 30 days';
    case 'all': return 'All time';
    default: return 'Selected period';
  }
}

// Loading Skeleton
const OverallStatsSkeletonBlock: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div>
        <div className="h-6 bg-gray-200 rounded-md w-48 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded-md w-64"></div>
      </div>
      <div className="h-8 bg-gray-200 rounded-md w-16"></div>
    </div>
    
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="h-8 bg-gray-200 rounded-md mb-2 mx-auto w-8"></div>
          <div className="h-3 bg-gray-200 rounded-md w-16 mb-2 mx-auto"></div>
          <div className="h-5 bg-gray-200 rounded-md w-12 mb-1 mx-auto"></div>
          <div className="h-3 bg-gray-200 rounded-md w-20 mx-auto"></div>
        </div>
      ))}
    </div>
    
    <div className="mt-6 pt-6 border-t border-gray-100">
      <div className="h-4 bg-gray-200 rounded-md w-24 mb-3"></div>
      <div className="flex gap-2">
        <div className="h-6 bg-gray-200 rounded-full w-32"></div>
        <div className="h-6 bg-gray-200 rounded-full w-28"></div>
        <div className="h-6 bg-gray-200 rounded-full w-36"></div>
      </div>
    </div>
  </div>
);

// Empty State
const EmptyOverallStatsBlock: React.FC<{ period: TimePeriod }> = ({ period }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <div className="text-center py-12">
      <div className="text-6xl mb-4">📊</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No Statistics Yet
      </h3>
      <p className="text-gray-600 mb-4">
        {period === 'all' 
          ? "Complete some practice sessions to see your statistics here."
          : `No practice sessions found for ${getPeriodDescription(period).toLowerCase()}.`
        }
      </p>
      <div className="text-sm text-gray-500">
        Start practicing to track your typing progress! 🚀
      </div>
    </div>
  </div>
);

export default OverallStatsBlock;